import React, { useEffect, useRef, useState } from 'react';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface GoogleMapsRouteProps {
  startCoords: Coordinates | null;
  endCoords: Coordinates | null;
}

declare global {
  // eslint-disable-next-line
  interface Window {
    google?: {
      maps: {
        Map: new (element: HTMLElement, options: any) => any;
        Marker: new (options: any) => any;
        Polyline: new (options: any) => any;
        LatLngBounds: new () => any;
        Geocoder: new () => any;
        MapsApiLoaded: Promise<void>;
        MapsEvent: any;
        InfoWindow: new (options: any) => any;
        places: any;
        geometry: {
          spherical: any;
        };
        SymbolPath: {
          CIRCLE: number;
        };
      };
    };
  }
}

export const GoogleMapsRoute: React.FC<GoogleMapsRouteProps> = ({
  startCoords,
  endCoords,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const startMarkerRef = useRef<any>(null);
  const endMarkerRef = useRef<any>(null);
  const polylineRef = useRef<any>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    const loadGoogleMaps = async () => {
      if (typeof window === 'undefined') return;

      if (!window.google?.maps && GOOGLE_MAPS_API_KEY) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          setMapLoaded(true);
        };
        script.onerror = () => {
          console.error('Failed to load Google Maps script');
        };
        document.head.appendChild(script);
      } else if (window.google?.maps) {
        setMapLoaded(true);
      }
    };

    loadGoogleMaps();
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;

    const google = window.google;
    if (!google?.maps) return;

    const map = new google.maps.Map(mapRef.current, {
      center: { lat: 51.505, lng: -0.09 },
      zoom: 13,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;

    const google = window.google;
    if (!google?.maps) return;

    if (startMarkerRef.current) {
      startMarkerRef.current.setMap(null);
    }
    if (endMarkerRef.current) {
      endMarkerRef.current.setMap(null);
    }
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
    }

    if (startCoords) {
      startMarkerRef.current = new google.maps.Marker({
        position: { lat: startCoords.lat, lng: startCoords.lng },
        map: mapInstanceRef.current,
        label: {
          text: 'A',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '16px',
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#28a745',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 3,
          scale: 15,
        },
        title: 'Start Location',
      });
    }

    if (endCoords) {
      endMarkerRef.current = new google.maps.Marker({
        position: { lat: endCoords.lat, lng: endCoords.lng },
        map: mapInstanceRef.current,
        label: {
          text: 'B',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '16px',
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#dc3545',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 3,
          scale: 15,
        },
        title: 'End Location',
      });
    }

    if (startCoords && endCoords) {
      const path = [
        { lat: startCoords.lat, lng: startCoords.lng },
        { lat: endCoords.lat, lng: endCoords.lng },
      ];

      polylineRef.current = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: '#007bff',
        strokeOpacity: 0.7,
        strokeWeight: 4,
        map: mapInstanceRef.current,
      });

      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: startCoords.lat, lng: startCoords.lng });
      bounds.extend({ lat: endCoords.lat, lng: endCoords.lng });
      mapInstanceRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50,
      });
    } else if (startCoords) {
      mapInstanceRef.current.setCenter({
        lat: startCoords.lat,
        lng: startCoords.lng,
      });
      mapInstanceRef.current.setZoom(13);
    } else if (endCoords) {
      mapInstanceRef.current.setCenter({
        lat: endCoords.lat,
        lng: endCoords.lng,
      });
      mapInstanceRef.current.setZoom(13);
    }
  }, [startCoords, endCoords, mapLoaded]);

  return (
    <div
      ref={mapRef}
      style={{
        height: '100%',
        width: '100%',
        borderRadius: '8px',
      }}
    />
  );
};
